import React, { useEffect, useState } from "react";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { useNavigate, useParams } from "react-router-dom";
import axios, { cookies, MEDIA_BASE_URL } from "../axios/axios";
import { ToastContainer, toast } from "react-toastify";

const RealEstateForm = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [file, setFile] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [loading, setLoading] = useState(false);

  const { id } = useParams();

  const [data, setData] = useState({
    price: "",
    unit_price: "",
    order_name: "",
    remove_cable: "",
    clean_swimmingpool: "",
    grass_replacement: "",
    add_sky: "",
    add_tvImage: "",
    add_fireplace: "",
    quantity: "",
    social_media: "",
    drive_link: "",
    additional_notes: "",
  });

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get("/services/getServices");
        setServices(response.data.services);
      } catch (error) {
        console.error("Error fetching services:", error);
        toast.error(error.response?.data?.message || "An error occurred");
      }
    };
    fetchServices();
  }, []);

  const newData = services.find(
    (val) => val.id.replaceAll(" ", "-").replaceAll("/", "_") === id
  );

  // helper: get numeric qty
  const getQuantity = () => Math.max(0, Number(data.quantity) || 0);

  // compute add-on total (each add-on adds $0.4 when 'yes')
  const computeAddons = (d) => {
    const addons = [
      "remove_cable",
      "clean_swimmingpool",
      "grass_replacement",
      "add_tvImage",
      "add_sky",
      "add_fireplace",
    ];
    return addons.reduce((sum, key) => sum + (d[key] === "yes" ? 0.4 : 0), 0);
  };

  const computeUnitPrice = (d) => {
    const base = Number(newData?.price) || Number(d.unit_price) || 0;
    return Number((base + computeAddons(d)).toFixed(2));
  };

  const computeTotalPrice = (unit, qty, discountPercent = 0) => {
    const base = Number(unit || 0) * Math.max(1, qty || 1);
    if (discountPercent) {
      return Number((base * (1 - discountPercent / 100)).toFixed(2));
    }
    return Number(base.toFixed(2));
  };

  useEffect(() => {
    if (newData) {
      setData((prev) => {
        const initial = {
          ...prev,
          unit_price: Number(newData.price) || 0,
          price: Number(newData.price) || 0,
          quantity: prev.quantity || "1",
        };
        const unit = computeUnitPrice(initial);
        const total = computeTotalPrice(
          unit,
          Number(initial.quantity || 1),
          isCouponApplied ? discount : 0
        );
        return { ...initial, unit_price: unit, price: total };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newData]);

  const dataHandler = (e) => {
    const { name, value } = e.target;

    if (name === "quantity") {
      const qty = Math.max(0, Math.floor(Number(value) || 0));

      setData((prev) => {
        const next = { ...prev, quantity: qty.toString() };
        const unit = computeUnitPrice(next);
        const total = computeTotalPrice(
          unit,
          qty,
          isCouponApplied ? discount : 0
        );
        return { ...next, unit_price: unit, price: total };
      });

      // enforce selected files count
      if (file && file.length > 0) {
        const selectedCount = file.length;
        if (qty === 1 && selectedCount > 1) {
          setFile([file[0]]);
          toast.info("Quantity set to 1 — extra selected files were removed.");
        } else if (qty > 0 && selectedCount > qty) {
          setFile(file.slice(0, qty));
          toast.info(
            `Quantity set to ${qty} — extra selected files were removed.`
          );
        }
      }

      return;
    }

    // for add-on selects and other fields
    setData((prev) => {
      const next = { ...prev, [name]: value };
      // if the changed field is one of the add-ons, recompute unit and total
      const addonsKeys = [
        "remove_cable",
        "clean_swimmingpool",
        "grass_replacement",
        "add_tvImage",
        "add_sky",
        "add_fireplace",
      ];
      if (addonsKeys.includes(name)) {
        const unit = computeUnitPrice(next);
        const qty = getQuantity();
        const total = computeTotalPrice(
          unit,
          qty,
          isCouponApplied ? discount : 0
        );
        return { ...next, unit_price: unit, price: total };
      }

      // default
      return next;
    });
  };

  const applyCoupon = async () => {
    try {
      const storedUser = cookies.get("user");
      const userId = storedUser?.user?.id || null;

      if (!userId) {
        toast.error("User not found. Please log in again.");
        return;
      }

      const response = await axios.post("/coupon/validate", {
        code: couponCode,
        userId,
      });

      if (response.data.valid) {
        setDiscount(response.data.discount);
        setIsCouponApplied(true);

        setData((prev) => {
          const unit = computeUnitPrice(prev);
          const qty = getQuantity() || 1;
          const total = computeTotalPrice(unit, qty, response.data.discount);
          return { ...prev, price: total };
        });

        toast.success("Coupon applied!");
      } else {
        setErrorMessage(response.data.message || "Coupon not valid");
        toast.error(response.data.message || "Coupon not valid");
      }
    } catch (error) {
      console.error("Error applying coupon:", error);
      setErrorMessage(
        error.response?.data?.message || "Error validating coupon."
      );
      toast.error(error.response?.data?.message || "An error occurred");
    }
  };

  const dataSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: res } = await axios.post(`/real-estate/${id}`, data);

      toast.success("Order submitted and images uploaded to OneDrive!");
      navigate(`/upload-files/${res.id}?type=real-estate`);
    } catch (error) {
      console.error(error);
      setErrorMessage(error.response?.data?.message || "An error occurred");
      toast.error(error.response?.data?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (!newData) {
    return <h2 className="text-center my-5">Loading ...</h2>;
  }

  const qty = getQuantity();
  const originalTotal = Number(data.unit_price || 0) * (qty || 1);

  return (
    <>
      <Navbar />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
      <div className="mx-5">
        <div className="row align-items-center py-5 ">
          <div className="col-md-6 my-4">
            <img
              className="w-100 object-fit-cover"
              height={600}
              src={`${MEDIA_BASE_URL}/${newData.image}`}
              crossOrigin="anonymous"
              alt=""
            />
          </div>
          <div className="col-md-6 my-4 ps-5">
            <h4 className="mb-3">{newData.title}</h4>

            <h5 className="mb-3">
              Price:{" "}
              {isCouponApplied ? (
                <>
                  <del className="text-danger me-2">
                    ${originalTotal.toFixed(2)}
                  </del>
                  <span className="text-success">
                    ${Number(data.price).toFixed(2)}
                  </span>
                </>
              ) : (
                <span className="text-dark">${originalTotal.toFixed(2)}</span>
              )}
            </h5>
            {/* Coupon Code Input */}
            <div className="row  g-3 align-items-center mb-1">
              <div className="col-md-4">
                <label className="fs-14 col-form-label">Coupon Code</label>
              </div>
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control shadow-none rounded-2 p-2"
                  placeholder="Enter Coupon"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  disabled={isCouponApplied}
                />
              </div>
              <button
                className="col-md-2 btn py-2 px-0 rounded-2 btn-secondary mt-2"
                onClick={applyCoupon}
                disabled={isCouponApplied}
              >
                Apply
              </button>
            </div>
            <form onSubmit={dataSubmit}>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Order Name
                  </label>
                </div>
                <div class="col-md-8">
                  <input
                    onChange={dataHandler}
                    type="text"
                    name="order_name"
                    value={data.order_name}
                    className="form-control shadow-none rounded-2 p-2"
                  />
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    ADD-ONS - Remove cables and wires
                  </label>
                </div>
                <div class="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="remove_cable"
                    value={data.remove_cable}
                    className="w-100 p-2 form-select"
                  >
                    <option value="">Select</option>
                    <option value="yes">Yes ($ 0.40)</option>
                    <option value="no">No</option>
                  </select>{" "}
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    ADD-ONS - Clean Swimming Pool
                  </label>
                </div>
                <div class="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="clean_swimmingpool"
                    value={data.clean_swimmingpool}
                    className="w-100 p-2 form-select"
                  >
                    <option value="">Select</option>
                    <option value="yes">Yes ($ 0.40)</option>
                    <option value="no">No</option>
                  </select>{" "}
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    ADD-ONS - Grass Replacement
                  </label>
                </div>
                <div class="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="grass_replacement"
                    value={data.grass_replacement}
                    className="w-100 p-2 form-select"
                  >
                    <option value="">Select</option>
                    <option value="yes">Yes ($ 0.40)</option>
                    <option value="no">No</option>
                  </select>{" "}
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    ADD-ONS - Add TV Image
                  </label>
                </div>
                <div class="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="add_tvImage"
                    value={data.add_tvImage}
                    className="w-100 p-2 form-select"
                  >
                    <option value="">Select</option>
                    <option value="yes">Yes ($ 0.40)</option>
                    <option value="no">No</option>
                  </select>{" "}
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    ADD-ONS - Add Sky
                  </label>
                </div>
                <div class="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="add_sky"
                    value={data.add_sky}
                    className="w-100 p-2 form-select"
                  >
                    <option value="">Select</option>
                    <option value="yes">Yes ($ 0.40)</option>
                    <option value="no">No</option>
                  </select>{" "}
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    ADD-ONS - Add Fireplace
                  </label>
                </div>
                <div class="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="add_fireplace"
                    value={data.add_fireplace}
                    className="w-100 p-2 form-select"
                  >
                    <option value="">Select</option>
                    <option value="yes">Yes ($ 0.40)</option>
                    <option value="no">No</option>
                  </select>{" "}
                </div>
              </div>
              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Social Media
                  </label>
                </div>
                <div class="col-md-8">
                  <select
                    onChange={dataHandler}
                    name="social_media"
                    value={data.social_media}
                    className="w-100 p-2 form-control"
                  >
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>{" "}
                </div>
              </div>
              <div className="row g-3 align-items-center mb-1">
                <div className="col-md-4">
                  <label className="fs-14 col-form-label">Quantity</label>
                </div>
                <div className="col-md-8">
                  <input
                    onChange={dataHandler}
                    name="quantity"
                    value={data.quantity}
                    type="number"
                    className="form-control shadow-none rounded-2 p-2"
                    min={1}
                  />
                </div>
              </div>

              <div class="row g-3 align-items-center mb-1">
                <div class="col-md-4">
                  <label for="inputPassword6" class="fs-14 col-form-label">
                    Aditional notes
                  </label>
                </div>
                <div class="col-md-8">
                  <textarea
                    onChange={dataHandler}
                    name="additional_notes"
                    value={data.additional_notes}
                    className="form-control shadow-none rounded-2 p-2"
                  ></textarea>{" "}
                </div>
              </div>
              <button
                type="submit"
                className="btn bg_maincolor text-white"
                disabled={loading}
              >
                {loading ? "Loading..." : "Order Now"}
              </button>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default RealEstateForm;
